import { type MouseEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { Portal } from '@air/primitive-portal';

import { CanvasExportDragBadge } from '../../../components/CanvasExportDragBadge';
import { useEditCanvasStore } from '../../../store';
import { CANVAS_NODE_EXPORT_DROP_TYPE, type CanvasNodeExportPayload } from '../../../utils/canvas-asset-drop';

type NodePosition = { id: string; position: { x: number; y: number } };

const DEFAULT_NODE_HEIGHT = 360;
const DEFAULT_NODE_WIDTH = 480;

/**
 * Detects CMD/CTRL pressed mid-drag and switches from a normal ReactFlow node
 * move into the workspace-export flow.
 *
 * Works alongside `useDuplicateByDragCommand` (alt+drag) inside `useCanvasHistory`.
 * Returns drag lifecycle handlers, a `renderBadge` ReactNode, and `isExportActive`.
 *
 * When export mode activates:
 * - Commits the partial move (nodes stay where they were when CMD was pressed)
 * - Dispatches the same CANVAS_NODE_EXPORT_DROP_TYPE custom events as DragToWorkspaceOverlay
 * - Freezes node positions at the committed location every frame
 * - Shows the CanvasExportDragBadge at the cursor
 *
 * CMD release cancels export mode (badge hides, nodes stay at committed positions).
 * Re-pressing CMD during the same drag re-enters export mode.
 */
export function useCmdExportDuringDrag() {
  const exportModeRef = useRef(false);
  const wasExportRef = useRef(false);
  const committedPositionsRef = useRef<NodePosition[]>([]);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [badgeState, setBadgeState] = useState<{
    payloads: CanvasNodeExportPayload[];
    initialX: number;
    initialY: number;
  } | null>(null);
  const [isExportActive, setIsExportActive] = useState(false);

  const buildPayloads = useCallback((): CanvasNodeExportPayload[] => {
    const nodes = useEditCanvasStore.getState().nodes;
    const eligiblePayloads: CanvasNodeExportPayload[] = [];

    for (const n of nodes) {
      if (!n.selected) {
        continue;
      }

      if (n.type === 'image' && n.data.media) {
        eligiblePayloads.push({
          fileName: n.data.fileName ?? 'Untitled',
          height: n.height ?? DEFAULT_NODE_HEIGHT,
          media: n.data.media,
          mediaType: 'image',
          width: n.width ?? DEFAULT_NODE_WIDTH,
        });
      } else if (n.type === 'video' && n.data.media) {
        eligiblePayloads.push({
          fileName: n.data.fileName ?? 'Untitled',
          height: n.height ?? DEFAULT_NODE_HEIGHT,
          media: n.data.media,
          mediaType: 'video',
          thumbnailUrl: n.data.thumbnailUrl,
          width: n.width ?? DEFAULT_NODE_WIDTH,
        });
      }
    }

    return eligiblePayloads;
  }, []);

  const enterExportMode = useCallback(
    (event: MouseEvent, currentNodes: { id: string; position: { x: number; y: number } }[]) => {
      exportModeRef.current = true;
      wasExportRef.current = true;

      // Snapshot current positions so the partial move (drag-start → here) is recorded in undo history
      committedPositionsRef.current = currentNodes.map((n) => ({ id: n.id, position: { ...n.position } }));

      document.dispatchEvent(new CustomEvent(CANVAS_NODE_EXPORT_DROP_TYPE, { detail: { phase: 'start' } }));

      const container = document.querySelector('.react-flow');
      container?.classList.add('cmd-export-dragging');

      const payloads = buildPayloads();
      setBadgeState({
        initialX: event.clientX,
        initialY: event.clientY,
        payloads,
      });
      setIsExportActive(true);
    },
    [buildPayloads],
  );

  const exitExportMode = useCallback((event: MouseEvent) => {
    exportModeRef.current = false;

    const container = document.querySelector('.react-flow');
    container?.classList.remove('cmd-export-dragging');

    setBadgeState(null);
    setIsExportActive(false);

    // Empty-payload drop tells the board listener to tear down its highlight state
    document.dispatchEvent(
      new CustomEvent(CANVAS_NODE_EXPORT_DROP_TYPE, {
        detail: {
          clientX: event.clientX,
          clientY: event.clientY,
          payloads: [],
          phase: 'drop',
        },
      }),
    );
  }, []);

  const onDragStart = useCallback((_event: MouseEvent) => {
    exportModeRef.current = false;
    wasExportRef.current = false;
    committedPositionsRef.current = [];
    setBadgeState(null);
    setIsExportActive(false);
  }, []);

  const onDrag = useCallback(
    (event: MouseEvent, nodes: { id: string; position: { x: number; y: number } }[], altDragActive: boolean) => {
      if (altDragActive) {
        return;
      }

      const isCmdHeld = event.metaKey || event.ctrlKey;

      // Releasing CMD cancels export so users can abort without completing the drop
      if (exportModeRef.current && !isCmdHeld) {
        exitExportMode(event);
      }

      // Allow entering/re-entering export at any point during the drag
      if (!exportModeRef.current && isCmdHeld) {
        const canvasMode = useEditCanvasStore.getState().canvasMode;
        if (canvasMode.mode !== 'default') {
          return;
        }

        const payloads = buildPayloads();
        if (payloads.length === 0) {
          return;
        }

        enterExportMode(event, nodes);
      }

      // Badge position is set via direct DOM mutation to avoid React re-renders on every mousemove frame
      if (exportModeRef.current) {
        if (badgeRef.current) {
          badgeRef.current.style.left = `${event.clientX}px`;
          badgeRef.current.style.top = `${event.clientY}px`;
        }

        document.dispatchEvent(
          new CustomEvent(CANVAS_NODE_EXPORT_DROP_TYPE, {
            detail: { clientX: event.clientX, clientY: event.clientY, phase: 'move' },
          }),
        );
      }

      // ReactFlow continues to move nodes as the cursor moves. Reset every frame so
      // nodes appear stationary at their committed positions. React 18 batching prevents flicker.
      if (wasExportRef.current && committedPositionsRef.current.length > 0) {
        useEditCanvasStore.getState().moveNodes(committedPositionsRef.current);
      }
    },
    [buildPayloads, enterExportMode, exitExportMode],
  );

  /**
   * Called on drag stop. Returns the committed positions if export was activated
   * during this drag (so the caller can record the partial move), or null if
   * this hook didn't handle the drag.
   */
  const onDragStop = useCallback(
    (event: MouseEvent, _nodes: { id: string; position: { x: number; y: number } }[]): NodePosition[] | null => {
      const wasExport = wasExportRef.current;
      const isActiveExport = exportModeRef.current;
      const committed = committedPositionsRef.current;

      const container = document.querySelector('.react-flow');
      container?.classList.remove('cmd-export-dragging');

      setBadgeState(null);
      setIsExportActive(false);
      exportModeRef.current = false;
      wasExportRef.current = false;
      committedPositionsRef.current = [];

      if (!wasExport) {
        return null;
      }

      // CMD still held at mouseup = user intends to drop into the workspace
      if (isActiveExport) {
        const payloads = buildPayloads();
        document.dispatchEvent(
          new CustomEvent(CANVAS_NODE_EXPORT_DROP_TYPE, {
            detail: {
              clientX: event.clientX,
              clientY: event.clientY,
              payloads,
              phase: 'drop',
            },
          }),
        );
      }

      // Final reset in case the last onDrag frame left nodes at a different position
      if (committed.length > 0) {
        useEditCanvasStore.getState().moveNodes(committed);
      }

      return committed;
    },
    [buildPayloads],
  );

  const renderBadge: ReactNode = useMemo(
    () =>
      badgeState ? (
        <Portal>
          <CanvasExportDragBadge
            initialX={badgeState.initialX}
            initialY={badgeState.initialY}
            payloads={badgeState.payloads}
            ref={badgeRef}
          />
        </Portal>
      ) : null,
    [badgeState],
  );

  return { isExportActive, onDrag, onDragStart, onDragStop, renderBadge };
}
